// The profile fields the builder offers (spec/dashboard.md section 7.1 and 5.5): every field of every profile root, grouped
// by profile › section, with the leaf template its type takes and the PascalCase path the engine reads.
import type { Parameter, Template } from '../../api/types';
import { CONDITION_NS } from './model';

export interface ProfileField {
  /** the profile the leaf names: the root template (SmartObjects.UnnyProfile) or a list element template inside a filter */
  profile: string;
  /** the dotted PascalCase path: GeneralInfo.Level */
  path: string;
  /** `Payments › Total spend` */
  label: string;
  group: string;
  type: Parameter['type'];
  /** the element template of a list field, for its filter */
  elementTemplate?: string | null;
  /** the leaf template this field takes */
  leaf: string;
}

/** `generalInfo` → `GeneralInfo`, `uTCTimeOffset` → `UTCTimeOffset`: the storage-cache property names the engine reads. */
export function pascal(name: string): string {
  return name.length === 0 ? name : name[0]!.toUpperCase() + name.slice(1);
}

export function leafTemplateFor(type: Parameter['type']): string | null {
  switch (type) {
    case 'int':
    case 'float':
    case 'duration':
    case 'date_time':
    case 'enum':
      return `${CONDITION_NS}ProfileFieldNumber`;
    case 'long':
      return `${CONDITION_NS}ProfileFieldLong`;
    case 'string':
    case 'localized_string':
    case 'document':
    case 'template_ref':
    case 'profile_path':
    case 'asset':
      return `${CONDITION_NS}ProfileFieldString`;
    case 'bool':
      return `${CONDITION_NS}ProfileFieldBool`;
    case 'list':
      return `${CONDITION_NS}ProfileFieldList`;
    default:
      return null;
  }
}

function words(name: string): string {
  return pascal(name).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

/** The fields of one template (a profile root or a list element template), recursing into nested profile data. */
export function fieldsOf(templates: Map<string, Template>, template: Template, profile: string, prefix = '', groupPrefix = '', depth = 0): ProfileField[] {
  const out: ProfileField[] = [];
  for (const p of template.effectiveParameters) {
    const path = prefix ? `${prefix}.${pascal(p.name)}` : pascal(p.name);
    const label = (groupPrefix ? `${groupPrefix} › ` : '') + words(p.displayName ?? p.name);
    if (p.type === 'profile_data' && p.template && depth < 6) {
      const nested = templates.get(p.template);
      if (nested) out.push(...fieldsOf(templates, nested, profile, path, label, depth + 1));
      continue;
    }
    const leaf = leafTemplateFor(p.type);
    if (!leaf) continue;
    out.push({ profile, path, label, group: groupPrefix || (template.displayName ?? template.name.split('.').pop() ?? template.name), type: p.type, elementTemplate: p.type === 'list' ? (p.of?.template ?? null) : null, leaf });
  }
  return out;
}

/** Every field of every profile root on the branch. */
export function profileFields(templates: Map<string, Template>): ProfileField[] {
  const out: ProfileField[] = [];
  for (const t of templates.values()) {
    if (t.kind !== 'profile_root') continue;
    out.push(...fieldsOf(templates, t, t.name, '', t.name === 'SmartObjects.UnnyProfile' ? 'System' : (t.displayName ?? t.name.split('.').pop() ?? t.name)));
  }
  return out;
}

/** The fields a list filter's leaves may read: those of the element template (the leaf names the element template as its profile). */
export function elementFields(templates: Map<string, Template>, elementTemplate: string): ProfileField[] {
  const t = templates.get(elementTemplate);
  return t ? fieldsOf(templates, t, elementTemplate, '', t.displayName ?? t.name.split('.').pop() ?? t.name) : [];
}
